import type { GetStaticProps, InferGetStaticPropsType } from 'next';
import type { ParsedUrlQuery } from 'querystring';
import Link from 'next/link';
import { NextSeo } from 'next-seo';
import clsx from 'clsx';

import {
  frontpageQuery,
  getNavigation,
  latestFrontpageIdQuery,
  populateFrontpageWithProductUrls,
} from '@/lib/sanity/queries';
import type { MaterializedFrontpage } from '@/lib/sanity/queries';
import { usePreviewSubscribtion } from '@/lib/sanity/hooks';
import { getPathFromInternalLink } from '@/lib/utils/getUrls';

import Layout from '@/components/layout';
import Debug from '@/components/debug';
import { ContentBlocksRenderer } from '@/components/contentBlocks/ContentBlocksRenderer';
import { configuredSanityClientWithoutCdn } from '@/lib/sanity/sanityClient';
import { getCatgories } from '@/lib/categories/getCategories';
import { ProductCategories } from '@/components/productCategories';

export type FrontpageProps = InferGetStaticPropsType<typeof getStaticProps>;

export const Frontpage = (props: FrontpageProps) => {
  const {
    frontpage: initialFrontpage,
    preview = false,
    previewData,
    categories,
  } = props;
  const id = previewData?.id || initialFrontpage._id;

  const frontpage = usePreviewSubscribtion<MaterializedFrontpage>({
    query: frontpageQuery,
    queryParams: { id },
    preview,
    previewData,
    initialData: initialFrontpage,
    // transformData: populateFrontpageWithImageUrls,
  });

  const { grid, blocks, topBanner, topContent } = frontpage;
  const topBannerLink =
    topBanner?.internalLink &&
    topBanner?.text &&
    getPathFromInternalLink(topBanner.internalLink, topBanner);
  return (
    <Layout loading={false} isPreview={preview}>
      <NextSeo description="Møbler - Høy kvalitet, gode priser" />
      {/* <ProductCategories categories={categories} rootPath="/rom/" /> */}
      <main className="mt-5 space-y-5">
        {topBannerLink && (
          <Link
            href={topBannerLink}
            className={clsx(
              'flex justify-center items-center',
              'overflow-hidden',
              'py-5',
              'hd-lg',
              'text-[5vw]',
              'sm:hd-xl',
              'md:hd-2xl',
              'lg:hd-4xl',
              'uppercase text-center',
              // 'aspect-[10/1]',
              'whitespace-nowrap mb-5',
              'hover:underline',
              {
                'bg-primary-50 text-neutral-0': topBanner.color === 'primary',
                'bg-secondary-50 text-neutral-0':
                  topBanner.color === 'secondary',
                'bg-tetiery-50 text-neutral-0': topBanner.color === 'tetiery',
                'bg-[#FDC300] text-neutral-80 bg-gradient-to-tr from-[#FDC300] to-[#F7A600]':
                  topBanner.color === 'blackweek',
                'bg-neutral-80 text-neutral-0':
                  topBanner.color === 'neutral' || !topBanner.color,
              }
            )}
          >
            {topBanner.text}
          </Link>
        )}
        {topContent && (
          <ContentBlocksRenderer
            priority={true}
            blocks={[...topContent]}
            getSize={(_, i) => (i === 0 ? 'lg' : 'md')}
          />
        )}

        <div className="frontpage-grid">
          <ContentBlocksRenderer
            priority={true}
            blocks={grid}
            // First grid is large
            getSize={(_, i) => (i === 0 ? 'lg' : 'md')}
          />
        </div>
        <div className="space-y-20 mt-20">
          <ContentBlocksRenderer blocks={blocks} />
        </div>
        <Debug data={{ preview, previewData }} label="Preview" />
        <Debug data={frontpage} label="Frontpage" />
        <Debug data={initialFrontpage} label="Initial Frontpage" />
        <Debug data={categories} label="categories" />
      </main>
    </Layout>
  );
};

export const getStaticProps: GetStaticProps<
  any,
  ParsedUrlQuery,
  { id: string }
> = async (context) => {
  const { preview = false, previewData = null } = context;
  const frontpageId = await configuredSanityClientWithoutCdn.fetch<String>(
    latestFrontpageIdQuery
  );

  const frontpage =
    await configuredSanityClientWithoutCdn.fetch<MaterializedFrontpage>(
      frontpageQuery,
      {
        id: frontpageId,
      }
    );

  // todo, populate products from productCollectionBlocks

  const navigation = await getNavigation();

  return {
    props: {
      frontpage: await populateFrontpageWithProductUrls(frontpage),
      categories: await getCatgories('ROM'),
      navigation,
      preview,
      previewData,
    },
    revalidate: 60 * 60,
  };
};

export default Frontpage;
